export type FilterDropdownKey = string | number | boolean;

export function normalizeFilterKey(key: FilterDropdownKey): string {
  return String(key);
}

export function isFilterKeySelected(
  currentValues: FilterDropdownKey[] | undefined,
  key: FilterDropdownKey
): boolean {
  const keyStr = normalizeFilterKey(key);
  return (currentValues ?? []).some((item) => normalizeFilterKey(item) === keyStr);
}

/** Toggle one value in a multi-select filter field (add if absent, remove if present). */
export function toggleFilterSelection(
  currentValues: FilterDropdownKey[],
  key: FilterDropdownKey
): FilterDropdownKey[] {
  const keyStr = normalizeFilterKey(key);
  const index = currentValues.findIndex((item) => normalizeFilterKey(item) === keyStr);

  if (index !== -1) {
    return currentValues.filter((_, itemIndex) => itemIndex !== index);
  }

  return [...currentValues, key];
}

/** Merge keys into a multi-select field without removing existing selections. */
export function addFilterSelections(
  currentValues: FilterDropdownKey[],
  keys: FilterDropdownKey[]
): FilterDropdownKey[] {
  return keys.reduce((values, key) => {
    if (isFilterKeySelected(values, key)) {
      return values;
    }
    return [...values, key];
  }, currentValues);
}

/** Remove the given keys from a multi-select field. */
export function removeFilterSelections(
  currentValues: FilterDropdownKey[],
  keys: FilterDropdownKey[]
): FilterDropdownKey[] {
  const keysToRemove = new Set(keys.map(normalizeFilterKey));
  return currentValues.filter((value) => !keysToRemove.has(normalizeFilterKey(value)));
}

export function areAllFilterKeysSelected(
  currentValues: FilterDropdownKey[] | undefined,
  keys: FilterDropdownKey[]
): boolean {
  if (keys.length === 0) return false;
  return keys.every((key) => isFilterKeySelected(currentValues, key));
}

export function areSomeFilterKeysSelected(
  currentValues: FilterDropdownKey[] | undefined,
  keys: FilterDropdownKey[]
): boolean {
  return keys.some((key) => isFilterKeySelected(currentValues, key));
}
